import { PaymentSources, PaymentTypes } from '@appTypes/enums';
import {
  DeleteUnitPaymentLogsRes,
  GetMembersBalanceDetailsReq,
  GetMembersBalanceDetailsRes,
  GetPaymentLogCategories,
  GetUnitPaymentLogsReq,
  GetUnitPaymentLogsRes,
  GetUserPaymentLogsQueryParams,
  GetUserPaymentLogsRes,
  PaymentLogUpdateQuery,
  PostPaymentLogRequest,
  PostPaymentLogResponse,
  PutUnitPaymentLogsRes,
} from '@appTypes/esb';
import { PutPaymentLogRequest } from '@modules/paymentLogs/types';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';

export const paymentLogsAPI = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint GET /advancements/{userId}/paymentLogs
     */
    getUserPaymentLogs: builder.query<
      GetUserPaymentLogsRes,
      {
        userId: number;
        params: GetUserPaymentLogsQueryParams;
      }
    >({
      query: arg => ({
        url: `/advancements/${arg.userId}/paymentLogs`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.PaymentLogQuery,
                id: arg.userId,
              },
            ]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/paymentLogs
     */
    getUnitPaymentLogs: builder.query<
      GetUnitPaymentLogsRes,
      {
        orgGuid: string;
        params: GetUnitPaymentLogsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/paymentLogs`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.PaymentLogQuery,
                id: `${arg.orgGuid}-${arg.params.paymentType}`,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/paymentLogSummary
     */
    GetUnitPaymentLogsSummary: builder.query<
      GetUnitPaymentLogsRes,
      {
        orgGuid: string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/paymentLogSummary`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.PaymentLogSummary,
                id: arg.orgGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /advancements/v2/organization/{organizationGuid}/balanceDetails
     */
    GetMembersPaymentLogsBalance: builder.query<
      GetMembersBalanceDetailsRes,
      {
        orgGuid: string;
        params: GetMembersBalanceDetailsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.orgGuid}/balanceDetails`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.PaymentLogBalance,
                id: arg.orgGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/users/paymentLogs
     */
    postPaymentLog: builder.mutation<
      PostPaymentLogResponse,
      PostPaymentLogRequest
    >({
      query: body => ({
        url: `/advancements/v2/users/paymentLogs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, error, arg) => {
        if (error) return [];
        const isUnitTransaction = arg.paymentLogs.some(
          el => el.source === PaymentSources.UNIT,
        );

        const userLogsTags = arg.paymentLogs
          .filter(el => el.source === PaymentSources.USER)
          .map(el => ({
            type: tagTypes.PaymentLogQuery,
            id: el.userId,
          }));

        const categoriesTag = {
          type: tagTypes.PaymentLogCategories,
          id: arg.organizationGuid,
        };

        const invalidated = isUnitTransaction
          ? [
              {
                type: tagTypes.PaymentLogSummary,
                id: arg.organizationGuid,
              },
              {
                type: tagTypes.PaymentLogQuery,
                id: `${arg.organizationGuid}-${arg.paymentLogs?.[0]?.paymentType}`,
              },
              categoriesTag,
            ]
          : [
              {
                type: tagTypes.PaymentLogSummary,
                id: arg.organizationGuid,
              },
              {
                type: tagTypes.PaymentLogBalance,
                id: arg.organizationGuid,
              },
              categoriesTag,
              ...userLogsTags,
            ];

        return invalidated;
      },
    }),
    /**
     * @esbEndpoint PUT /advancements/v2/organization/{organizationGuid}/paymentLogs/{paymentLogId}
     */
    putPaymentLog: builder.mutation<
      PutUnitPaymentLogsRes,
      PutPaymentLogRequest
    >({
      query: ({ organizationGuid, paymentLogId, payload }) => ({
        url: `/advancements/v2/organization/${organizationGuid}/paymentLogs/${paymentLogId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (
        _result,
        error,
        { userId, organizationGuid, paymentType },
      ) => {
        if (error) return [];

        const invalidated = [
          {
            type: tagTypes.PaymentLogSummary,
            id: organizationGuid,
          },
          {
            type: tagTypes.PaymentLogBalance,
            id: organizationGuid,
          },
          ...(userId
            ? [
                {
                  type: tagTypes.PaymentLogQuery,
                  id: userId,
                },
              ]
            : []),
          ...(paymentType
            ? [
                {
                  type: tagTypes.PaymentLogQuery,
                  id: `${organizationGuid}-${paymentType}`,
                },
              ]
            : []),
        ];

        return invalidated;
      },
    }),
    /**
     * @esbEndpoint DELETE /advancements/v2/organization/{organizationGuid}/paymentLogs/{paymentLogId}
     */
    deletePaymentLog: builder.mutation<
      DeleteUnitPaymentLogsRes,
      PaymentLogUpdateQuery
    >({
      query: ({ organizationGuid, paymentLogId }) => ({
        url: `/advancements/v2/organization/${organizationGuid}/paymentLogs/${paymentLogId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (
        _result,
        error,
        { userId, organizationGuid, paymentType },
      ) => {
        if (error) return [];

        const paymentTypeTags =
          paymentType === 'All'
            ? [
                {
                  type: tagTypes.PaymentLogQuery,
                  id: `${organizationGuid}-${PaymentTypes.PAYMENT}`,
                },
                {
                  type: tagTypes.PaymentLogQuery,
                  id: `${organizationGuid}-${PaymentTypes.CHARGE}`,
                },
              ]
            : [
                {
                  type: tagTypes.PaymentLogQuery,
                  id: `${organizationGuid}-${paymentType}`,
                },
              ];

        const invalidated = [
          {
            type: tagTypes.PaymentLogSummary,
            id: organizationGuid,
          },
          {
            type: tagTypes.PaymentLogBalance,
            id: organizationGuid,
          },
          ...(userId
            ? [
                {
                  type: tagTypes.PaymentLogQuery,
                  id: userId,
                },
              ]
            : []),
          ...(paymentType ? paymentTypeTags : []),
        ];

        return invalidated;
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/{unitId}/paymentLogCategories
     */
    getPaymentLogCategories: builder.query<
      GetPaymentLogCategories,
      { unitId: number; organizationGuid: string }
    >({
      query: ({ unitId }) => ({
        url: `/organizations/v2/units/${unitId}/paymentLogCategories `,
        method: 'GET',
      }),
      providesTags: (result, error, { organizationGuid }) =>
        result && !error
          ? [
              {
                type: tagTypes.PaymentLogCategories,
                id: organizationGuid,
              },
            ]
          : [],
    }),
  }),
});

export const {
  useGetUserPaymentLogsQuery,
  useGetUnitPaymentLogsQuery,
  useLazyGetUnitPaymentLogsQuery,
  usePostPaymentLogMutation,
  usePutPaymentLogMutation,
  useDeletePaymentLogMutation,
  useGetUnitPaymentLogsSummaryQuery,
  useLazyGetUnitPaymentLogsSummaryQuery,
  useGetMembersPaymentLogsBalanceQuery,
  useGetPaymentLogCategoriesQuery,
} = paymentLogsAPI;
